import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Heart, ExternalLink, ArrowRight, Download, Mail, 
  CheckCircle, FileText, Calendar, MapPin
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { getNewsArticlePath, NEWS_FALLBACK_ARTICLES, sortNewsByDateDesc, resolveNewsImageUrl } from '../utils/newsArticle';
import { REGISTERED_CHARITY_LABEL } from '../constants/charity';
import { PHONE_NUMBER, PHONE_TEL_HREF } from '../constants/contact';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CPRE Volunteer Hub URL for Rutland & Lincolnshire
const VOLUNTEER_HUB_URL = 'https://volunteer.cpre.org.uk/opportunities/results#team=4203&display=grid&s=date_advertised&o=desc&limit=14&include=image&public_search=true';

const VolunteeringPage = () => {
  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`, { params: { limit: 50 } });
        const latest = sortNewsByDateDesc(response.data || []).slice(0, 4);
        setNews(latest.length > 0 ? latest : sortNewsByDateDesc(NEWS_FALLBACK_ARTICLES).slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch news:', error);
        setNews(sortNewsByDateDesc(NEWS_FALLBACK_ARTICLES).slice(0, 4));
      } finally {
        setLoadingNews(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <Layout>
      <div data-testid="volunteering-page">
        {/* Hero Section - Simple like Leicestershire */}
        <section className="relative">
          <div className="h-[300px] md:h-[400px] overflow-hidden">
            <img
              src="/images/cpre images for website/27.jpg"
              alt="Volunteers working together"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cpre-new-yew/80 via-cpre-new-yew/40 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <div className="container-custom pb-12">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white">Opportunities & Volunteering</h1>
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="max-w-4xl">
              {/* Intro Text */}
              <p className="text-xl text-cpre-new-yew leading-relaxed mb-4">
                Volunteers are at the heart of CPRE Rutland & Lincolnshire. Our strength lies with your skills, energy and drive.
              </p>
              <p className="text-sm text-cpre-new-yew/70 mb-12">{REGISTERED_CHARITY_LABEL}</p>

              {/* Opportunities & Volunteering opportunities Box */}
              <div id="volunteer-opportunities" className="bg-cpre-pebble/50 rounded-2xl p-8 mb-12" data-testid="volunteer-opportunities-cta">
                <h2 className="font-heading text-2xl font-bold text-cpre-new-yew mb-4">
                  Opportunities & Volunteering
                </h2>
                <p className="text-cpre-new-yew/80 mb-6">
                  Search our current opportunities to find one that's just right for you.
                </p>
                <a 
                  href={VOLUNTEER_HUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    className="bg-cpre-new-yew hover:bg-cpre-new-yew/90 text-white rounded-full px-8 h-12"
                    data-testid="find-opportunity-btn"
                  >
                    Find your volunteering opportunity
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>

              {/* Volunteer with CPRE Section */}
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-cpre-new-yew mb-6">
                  Volunteer with CPRE Rutland & Lincolnshire
                </h2>
                
                <div className="prose prose-lg max-w-none text-cpre-new-yew/80">
                  <p className="mb-4">
                    From the tranquil waters of Rutland Water to the rolling hills of the Lincolnshire Wolds, 
                    we work to protect the beauty and tranquillity of our region's green spaces and to promote 
                    a sustainable and living countryside for future generations.
                  </p>
                  
                  <p className="mb-4 font-semibold text-cpre-new-yew">
                    Volunteers are wanted right now! Are you passionate about protecting and promoting 
                    Rutland and Lincolnshire's beautiful countryside? Do you have the time, skills, 
                    experience and knowledge to help us?
                  </p>
                  
                  <p className="mb-4">
                    We are always seeking volunteers. We have an ambitious strategic plan to increase our 
                    activities and raise our profile, so are looking for more volunteers to help with our work.
                  </p>
                  
                  <p className="mb-4">
                    You do not need to be a planning expert (few of us are!) as there are many other activities 
                    on which we need help. Contributions and expertise vary enormously. It does not matter how 
                    much time you can give – whether you make a regular commitment or just help out for the 
                    occasional hour, every little helps.
                  </p>
                </div>

                {/* Second CTA */}
                <div className="mt-8">
                  <a 
                    href={VOLUNTEER_HUB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button 
                      className="bg-cpre-new-yew hover:bg-cpre-new-yew/90 text-white rounded-full px-8 h-12"
                    >
                      Find your volunteering opportunity
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>

              {/* Further Information Section */}
              <div className="bg-white border border-stone-200 rounded-2xl p-8 mb-12" data-testid="further-info">
                <h2 className="font-heading text-2xl font-bold text-cpre-new-yew mb-6">
                  Further information
                </h2>
                
                <div className="prose prose-lg max-w-none text-cpre-new-yew/80 mb-6">
                  <p className="mb-4">
                    We want to give you a first-class experience when you volunteer with us. Our online 
                    volunteering hub is the one-stop place for all your volunteering information. You can 
                    apply for an opportunity using an easy online form and track your application every step 
                    of the way.
                  </p>
                  
                  <p className="mb-4">
                    We'd love to hear from you if you have other skills to offer or any questions about 
                    volunteering. You can reach us at{' '}
                    <a 
                      href="mailto:chair@cprerutlandandlincs.uk" 
                      className="text-cpre-new-yew hover:text-cpre-hill underline"
                    >
                      chair@cprerutlandandlincs.uk
                    </a>.
                  </p>
                  
                  <p>
                    We want you to have an excellent volunteering experience with us and to achieve this 
                    we've made{' '}
                    <a 
                      href="https://www.cpre.org.uk/wp-content/uploads/2020/03/CPRE-Volunteering-Commitments.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cpre-new-yew hover:text-cpre-hill underline inline-flex items-center gap-1"
                    >
                      commitments to our volunteers
                      <ExternalLink className="w-3 h-3" />
                    </a>{' '}
                    (PDF 860 KB).
                  </p>
                </div>

                {/* Quick Links */}
                <div className="flex flex-wrap gap-4 pt-4 border-t border-stone-200">
                  <a 
                    href="https://www.cpre.org.uk/wp-content/uploads/2020/03/CPRE-Volunteering-Commitments.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-cpre-new-yew hover:text-cpre-hill transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span className="text-sm font-medium">Opportunities & Volunteering Commitments PDF</span>
                  </a>
                  <a 
                    href="mailto:chair@cprerutlandandlincs.uk"
                    className="inline-flex items-center gap-2 text-cpre-new-yew hover:text-cpre-hill transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">Contact Us</span>
                  </a>
                </div>
              </div>

              {/* Trustee Vacancies - Priority Section */}
              <div className="mb-12 p-8 bg-cpre-autumn-leaf/10 border-2 border-cpre-autumn-leaf rounded-2xl" data-testid="trustee-vacancies">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-cpre-autumn-leaf rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <span className="inline-block px-3 py-1 bg-cpre-autumn-leaf text-white text-xs font-semibold rounded-full mb-2">
                      VACANCIES
                    </span>
                    <h2 className="font-heading text-2xl font-bold text-cpre-new-yew">
                      Trustee Vacancies
                    </h2>
                  </div>
                </div>
                
                <div className="prose prose-lg max-w-none text-cpre-new-yew/80 mb-6">
                  <p className="mb-4">
                    We are currently seeking committed individuals to join our Board of Trustees 
                    and help guide the future of CPRE Rutland & Lincolnshire.
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-200">
                    <CheckCircle className="w-5 h-5 text-cpre-autumn-leaf flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-cpre-new-yew">Treasurer</h4>
                      <p className="text-sm text-muted-foreground">
                        We urgently need a Treasurer to oversee our financial management and reporting. 
                        Experience in charity finance or accountancy preferred.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-200">
                    <CheckCircle className="w-5 h-5 text-cpre-new-yew flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-cpre-new-yew">Trustees (Planning Experience Preferred)</h4>
                      <p className="text-sm text-muted-foreground">
                        We welcome trustees with planning experience who can help us respond effectively 
                        to development proposals across Rutland and Lincolnshire.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <a href={PHONE_TEL_HREF}>
                    <Button className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-6">
                      <Heart className="w-4 h-4 mr-2" />
                      Call: {PHONE_NUMBER}
                    </Button>
                  </a>
                  <a href="mailto:chair@cprerutlandandlincs.uk?subject=Trustee Vacancy Enquiry">
                    <Button variant="outline" className="border-cpre-autumn-leaf text-cpre-autumn-leaf hover:bg-cpre-autumn-leaf hover:text-white rounded-full px-6">
                      <Mail className="w-4 h-4 mr-2" />
                      Email Us
                    </Button>
                  </a>
                </div>
              </div>

              {/* Volunteer Roles Overview */}
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-cpre-new-yew mb-6">
                  Types of volunteering roles
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      title: 'Planning Volunteers',
                      description: 'Help review and respond to planning applications that may impact our countryside.',
                      icon: FileText,
                    },
                    {
                      title: 'Countryside Wardens',
                      description: 'Monitor and maintain local footpaths and report issues to keep our paths accessible.',
                      icon: MapPin,
                    },
                    {
                      title: 'Event Helpers',
                      description: 'Support our public events, guided walks, and community engagement activities.',
                      icon: Calendar,
                    },
                    {
                      title: 'Trustees & Committee',
                      description: 'Help guide the strategic direction of CPRE Rutland & Lincolnshire.',
                      icon: Users,
                    },
                  ].map((role, index) => (
                    <div 
                      key={index}
                      className="flex gap-4 p-6 bg-cpre-heritage-white rounded-xl border border-stone-200 hover:shadow-md transition-shadow"
                    >
                      <div className="w-12 h-12 bg-cpre-new-yew/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <role.icon className="w-6 h-6 text-cpre-new-yew" />
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-cpre-new-yew mb-1">{role.title}</h4>
                        <p className="text-sm text-cpre-new-yew/70">{role.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 text-center">
                  <a 
                    href={VOLUNTEER_HUB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button 
                      size="lg"
                      className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-10"
                    >
                      Browse All Opportunities
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Latest News Section */}
        <section className="section-padding bg-cpre-pebble/30">
          <div className="container-custom">
            <h2 className="font-heading text-2xl font-bold text-cpre-new-yew mb-8">
              Latest news
            </h2>

            {loadingNews ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-64 bg-stone-200 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : news.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {news.map((article, index) => (
                  <Link key={article.id} to={getNewsArticlePath(article)}>
                    <Card className="bg-white border-0 overflow-hidden group hover:shadow-lg transition-shadow h-full">
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={resolveNewsImageUrl(article.image_url) || '/images/cpre images for website/13.jpg'}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <CardContent className="p-4">
                        <Badge className="mb-2 bg-cpre-pebble/50 text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white text-xs">
                          {article.category}
                        </Badge>
                        <h4 className="font-heading font-semibold text-sm text-cpre-new-yew line-clamp-2 group-hover:text-cpre-hill transition-colors">
                          {article.title}
                        </h4>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No news articles available.</p>
            )}

            <div className="mt-8">
              <Link to="/news">
                <Button variant="outline" className="border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full">
                  View all news
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold text-cpre-new-yew mb-4">
                Subscribe to our newsletter
              </h2>
              <p className="text-cpre-new-yew/70 mb-6">
                We'll send you the latest news and information on our work, campaigns, fundraising, 
                events and other ways you can support our vision for a thriving countryside.
              </p>
              <p className="text-sm text-cpre-new-yew/60">
                We hold and manage your details in accordance with the Data Protection Act 2018. 
                We won't share, sell or swap your information with other organisations for their 
                own marketing purposes. You can read our{' '}
                <Link to="/privacy" className="underline hover:text-cpre-new-yew">
                  Privacy Policy
                </Link>{' '}
                page for more information.
              </p>
            </div>
          </div>
        </section>

        {/* Footer Hill Curve - Like county sites */}
        <div className="bg-cpre-new-yew">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto -mb-1">
            <path d="M0 60V30C240 0 480 0 720 15C960 30 1200 45 1440 30V60H0Z" fill="white"/>
          </svg>
        </div>
      </div>
    </Layout>
  );
};

export default VolunteeringPage;

